// src/hooks/useContainerWidth.js
import api from "../config/axiosConfig";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import { convertToFormData } from "../config/convertToFormData";
import * as Yup from "yup";
import { useRfi } from "../contexts/RfiContext";
import { useSelector } from "react-redux";
import { hasPermission } from "../utils/utilityFunctions";
import { actionType } from "../utils/constant";

export default function useAssemblyStage(stageId, isAPICallAllowed) {
  const rfi_id = useParams().rfinumber;
  const { profile } = useSelector((state) => state.profile);

  const { getRFIStages, submitStageAPI, setAssemblyDataContext } = useRfi();
  const [assemblyData, setAssemblyData] = useState({});
  const [loading, setLoading] = useState(false);

  const validationSchema = Yup.object({
    documents: Yup.array()
      .min(1, "Please upload at least one document")
      .required("Documents are required"),
    visual_parts: Yup.array()
      .of(
        Yup.object({
          part_name: Yup.string().trim().required("Part name is required"),
          sticker_pasted: Yup.boolean().oneOf(
            [true],
            "Sticker must be pasted for all parts"
          ),
        })
      )
      .min(1, "Please add at least one part for visual check")
      .required("Visual check parts are required"),
  });

  const initialValues = {
    documents: assemblyData?.assembly_documents || [],
    visual_parts: assemblyData?.visual_parts || [],
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    if (!rfi_id) return;
    if (!hasPermission(profile?.permissions?.actions, actionType.stage_edit)) {
      toast.error("You don't have permission to send for approval");
      return;
    }
    setSubmitting(true);
    const formData = convertToFormData({ ...values, rfi_id });

    if (assemblyData?.assembly_id) {
      const updatedPartsToUpdate = values.visual_parts?.map((part) => {
        return {
          ...(part?.visual_part_id && { visual_part_id: part.visual_part_id }),
          part_name: part.part_name,
          sticker_pasted: part.sticker_pasted,
        };
      });

      await updateVisualPartApi({ visual_parts: updatedPartsToUpdate });
    } else {
      await createAssemblyDataAPI(formData);
    }
    await getAssemblyDataAPI(rfi_id);

    setSubmitting(false);
    await submitStageAPI({ stage_id: stageId });
    await getRFIStages(rfi_id);
  };

  // Delete part using Formik
  const handleDeletePart = async (part, values, setFieldValue) => {
    if (!hasPermission(profile?.permissions?.actions, actionType.stage_edit)) {
      toast.error("You don't have permission to delete part");
      return;
    }
    if (part.visual_part_id) {
      await deleteVisualPartApi(part.visual_part_id);
      const updatedParts = values.visual_parts.filter(
        (p) => p.visual_part_id !== part.visual_part_id
      );
      setFieldValue("visual_parts", updatedParts);
    } else {
      const updatedParts = values.visual_parts.filter((_, i) => i !== part.idx);
      setFieldValue("visual_parts", updatedParts);
    }
  };

  // Stage 2 API's below.

  const getAssemblyDataAPI = useCallback(
    async (rfiId) => {
      if (
        !hasPermission(profile?.permissions?.actions, actionType.stage_view)
      ) {
        toast.error("You don't have permission to view assembly data");
        return;
      }
      setLoading(true);
      await api
        .get(`/api/assembly/${rfiId}`)
        .then((res) => {
          const baseUrl = process.env.REACT_APP_API_BASE_URL;
          const desiredData = {
            ...res.data,
            assembly_documents: res.data.assembly_documents.map((doc) => {
              return {
                ...doc,
                document: baseUrl + doc.document,
              };
            }),
          };
          console.log("Assembly data", desiredData);

          setAssemblyData(desiredData);
          setAssemblyDataContext(desiredData?.visual_parts || []);
          return true;
        })
        .catch((err) => {
          console.log("Assembly data error", err);
          return false;
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [profile?.permissions?.actions, setAssemblyDataContext]
  );

  const createAssemblyDataAPI = async (values) => {
    if (!hasPermission(profile?.permissions?.actions, actionType.stage_edit)) {
      toast.error("You don't have permission to create assembly data");
      return;
    }
    return await api
      .post(`/api/assembly`, values)
      .then((res) => {
        setAssemblyData(res.data);
        toast.success("Assembly data created successfully!");
        return true;
      })
      .catch((err) => {
        console.log("Assembly data error", err);
        toast.error("Assembly data error!");
        return false;
      });
  };

  const uploadAssemblyDocuments = useCallback(
    async (files) => {
      if (
        !hasPermission(profile?.permissions?.actions, actionType.stage_edit)
      ) {
        toast.error("You don't have permission to upload documents");
        return;
      }
      const formData = convertToFormData({ documents: files });

      return await api
        .post(`/api/assembly/${assemblyData?.assembly_id}/documents`, formData)
        .then((response) => {
          return response;
        })
        .catch((error) => {
          console.error("Document upload failed:", error);
          toast.error("Document upload failed!");
        });
    },
    [assemblyData?.assembly_id, profile?.permissions?.actions]
  );

  // const addNewVisualPartApi = (newParts) => {
  //   api
  //     .post(`/api/assembly/${assemblyData?.assembly_id}/parts`, {
  //       visual_parts: newParts,
  //     })
  //     .then((response) => {
  //       console.log("Visual part added successfully:", response.data);
  //       toast.success("Visual part added successfully!");
  //     })
  //     .catch((error) => {
  //       console.error("Failed to add visual part:", error);
  //       toast.error("Failed to add visual part!");
  //     });
  // };

  const updateVisualPartApi = async (updatedParts) => {
    if (!hasPermission(profile?.permissions?.actions, actionType.stage_edit)) {
      toast.error("You don't have permission to update visual part");
      return;
    }
    return await api
      .patch(`/api/assembly/${assemblyData?.assembly_id}/remarks`, updatedParts)
      .then(() => {
        toast.success("Visual part updated successfully!");
        return true;
      })
      .catch((error) => {
        console.error("Failed to update visual part:", error);
        toast.error("Failed to update visual part!");
        return false;
      });
  };

  const deleteVisualPartApi = async (visualPartId) => {
    if (!hasPermission(profile?.permissions?.actions, actionType.stage_edit)) {
      toast.error("You don't have permission to delete visual part");
      return;
    }
    try {
      await api.delete(`/api/assembly/parts/${visualPartId}`);
      toast.success("Visual part deleted successfully!");
      return true;
    } catch (error) {
      console.error("Failed to delete visual part:", error);
      toast.error("Failed to delete visual part!");
      return false;
    }
  };

  const deleteDocumentApi = async (documentId) => {
    if (!hasPermission(profile?.permissions?.actions, actionType.stage_edit)) {
      toast.error("You don't have permission to delete document");
      return;
    }
    return await api
      .delete(`/api/assembly/documents/${documentId}`)
      .then(() => {
        toast.success("Document deleted successfully!");
        return true;
      })
      .catch((error) => {
        console.error("Failed to delete document:", error);
        toast.error("Failed to delete document!");
        return false;
      });
  };

  const handleFilesSelect = useCallback(
    (files, setFieldValue, currentFiles) => {
      if (
        !hasPermission(profile?.permissions?.actions, actionType.stage_edit)
      ) {
        toast.error("You don't have permission to upload documents");
        return;
      }
      const totalFiles = (currentFiles || []).length + files.length;
      if (totalFiles > 5) {
        toast.error("Maximum 5 files allowed");
        return;
      }

      // Use the latest ndtData value from state
      if (files?.length > 0 && assemblyData?.assembly_id) {
        uploadAssemblyDocuments(files)
          .then((response) => {
            const baseUrl = process.env.REACT_APP_API_BASE_URL;
            const currentlyUploadeDoc = response.data.documents.map((doc) => {
              return {
                ...doc,
                document: baseUrl + doc.document,
              };
            });
            const newFiles = [...(currentFiles || []), ...currentlyUploadeDoc];
            setFieldValue("documents", newFiles);
          })
          .catch((error) => {
            console.error("Document upload failed:", error);
            toast.error("Document upload failed!");
          });
      } else {
        const newFiles = [...(currentFiles || []), ...files];
        setFieldValue("documents", newFiles);
      }
    },
    [
      assemblyData?.assembly_id,
      uploadAssemblyDocuments,
      profile?.permissions?.actions,
    ]
  );

  const handleDocumentDelete = async (file, setFieldValue, currentFiles) => {
    if (!hasPermission(profile?.permissions?.actions, actionType.stage_edit)) {
      toast.error("You don't have permission to delete document");
      return;
    }
    if (file?.document_id) {
      await deleteDocumentApi(file.document_id);
      const updatedFiles = currentFiles.filter(
        (cfile) => cfile.document_id !== file.document_id
      );
      setFieldValue("documents", updatedFiles);
    } else {
      const updatedFiles = currentFiles.filter(
        (cfile) => cfile.name !== file.name
      );
      setFieldValue("documents", updatedFiles);
    }
  };

  useEffect(() => {
    if (!rfi_id || !isAPICallAllowed) return;
    if (!hasPermission(profile?.permissions?.actions, actionType.stage_view))
      return;
    getAssemblyDataAPI(rfi_id);
  }, [
    getAssemblyDataAPI,
    rfi_id,
    isAPICallAllowed,
    profile?.permissions?.actions,
  ]);

  return {
    loading,
    initialValues,
    validationSchema,
    handleSubmit,
    handleFilesSelect,
    handleDocumentDelete,
    handleDeletePart,
  };
}
